import type { SearchHit } from '@contracts';
import { ArrowDown, ArrowUp, ChevronRight } from 'lucide-react';
import { type KeyboardEvent, type ReactNode, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router';
import { DeviceIdentity } from '../devices/DeviceIdentity';
import { recordContext, useScope } from '../scope/scope';
import type { Sort, SortField } from '../search/state';
import { kindIcon, Highlight } from '../shell/CommandPalette';
import { cn } from '../ui/cn';
import { Skeleton, TimeAgo } from '../ui/misc';
import { OsText, StatusBadge } from '../ui/status';
import { recordPath } from './paths';

const wide = () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1280px)').matches;

interface Column { key: string; label: string; sort?: SortField; className?: string; render: (hit: SearchHit) => ReactNode }

export function RecordTable({ hits, mode, loading, dimmed, sort, defaultSort, onSort, selectedId, onSelect, citations, highlight = '', caption, where = 'full' }: {
  hits: SearchHit[]; mode: 'device' | 'mixed'; loading: boolean; dimmed?: boolean;
  sort: Sort | null; defaultSort: Sort; onSort?: (sort: Sort | null) => void;
  selectedId?: string | null; onSelect?: (hit: SearchHit | null) => void; citations?: Map<string, number>; highlight?: string; caption: string;
  /** Inside one customer the customer name is redundant; inside one site, so is the site. */
  where?: 'full' | 'site' | 'none';
}) {
  const { mspId, remember } = useScope();
  const navigate = useNavigate();
  const rows = useRef<Array<HTMLElement | null>>([]);
  const open = (hit: SearchHit) => { remember(hit); navigate(recordPath(mspId, hit.kind, hit.id)); };
  const activate = (hit: SearchHit) => (onSelect && wide() ? onSelect(hit.id === selectedId ? null : hit) : open(hit));
  const effective = sort ?? defaultSort;

  useEffect(() => { if (selectedId && !hits.some(h => h.id === selectedId)) onSelect?.(null); }, [hits, selectedId, onSelect]);

  const onKey = (e: KeyboardEvent, index: number, hit: SearchHit) => {
    const move = (to: number) => { e.preventDefault(); const row = rows.current[Math.max(0, Math.min(hits.length - 1, to))]; row?.focus(); };
    if (e.key === 'ArrowDown') move(index + 1);
    else if (e.key === 'ArrowUp') move(index - 1);
    else if (e.key === 'Home') move(0);
    else if (e.key === 'End') move(hits.length - 1);
    else if (e.key === 'Enter') { e.preventDefault(); open(hit); }
    else if (e.key === ' ') { e.preventDefault(); activate(hit); }
    else if (e.key === 'Escape' && selectedId) { e.preventDefault(); onSelect?.(null); }
  };

  const title = (hit: SearchHit) => {
    const Icon = kindIcon[hit.kind];
    return (
      <span className="block min-w-0"><span className="flex min-w-0 items-center gap-2.5">
        {mode === 'mixed' && <Icon className="size-4 shrink-0 text-ink-3" />}
        <Link to={recordPath(mspId, hit.kind, hit.id)} onClick={e => { e.stopPropagation(); remember(hit); }} tabIndex={-1}
          className={cn('truncate font-medium text-ink hover:underline', hit.kind === 'device' && 'font-mono text-[13px]')}>
          <Highlight text={hit.title} query={highlight} />
        </Link>
      </span>{hit.kind === 'device' && <DeviceIdentity device={hit} />}</span>
    );
  };
  const columns: Column[] = mode === 'device' ? [
    { key: 'status', label: 'Status', sort: 'status', className: 'w-[132px]', render: h => <StatusBadge status={h.status} /> },
    { key: 'title', label: 'Device', sort: 'title', render: h => (
      <div className="min-w-0">
        <span className="flex min-w-0 items-center gap-1.5">{title(h)}{h.managed === false && <span className="shrink-0 rounded bg-sunken px-1 text-[10.5px] font-medium text-ink-3" title="Inventory record only; no agent reports for it">No agent</span>}</span>
        {h.signals[0] ? <div className={cn('truncate text-[12px]', h.signals[0].severity === 'critical' ? 'text-offline' : h.signals[0].severity === 'warning' ? 'text-attention' : 'text-ink-3')}>
          {h.signals[0].title}{h.signals.length > 1 ? ` +${h.signals.length - 1}` : ''}</div>
          : h.serial && <div className="truncate font-mono text-[11.5px] text-ink-3">{h.serial}</div>}
      </div>) },
    ...(where === 'none' ? [] : [{ key: 'where', label: where === 'site' ? 'Site' : 'Customer · Site',
      render: (h: SearchHit) => <span className="truncate text-ink-2">{where === 'site' ? h.siteName : recordContext(h)}</span> }]),
    { key: 'os', label: 'OS', className: 'w-[110px]', render: h => <OsText os={h.os} /> },
    { key: 'seen', label: 'Last check-in', sort: 'lastSeenAt', className: 'w-[150px]', render: h => <TimeAgo value={h.lastSeenAt} /> },
  ] : [
    { key: 'title', label: 'Record', sort: 'title', render: title },
    { key: 'where', label: 'Belongs to', render: h => <span className="truncate text-ink-2">{h.kind === 'organization' ? <span className="text-ink-3">Customer</span> : recordContext(h)}</span> },
    { key: 'status', label: 'Status', className: 'w-[132px]', render: h => (h.kind === 'device' ? <StatusBadge status={h.status} /> : <span className="text-ink-3">—</span>) },
    { key: 'updated', label: 'Record updated', sort: 'updatedAt', className: 'w-[150px]', render: h => <TimeAgo value={h.source.updatedAt} className="!text-ink-2" /> },
  ];

  const toggleSort = (field: SortField) => {
    if (!onSort) return;
    if (effective.field !== field) onSort({ field, direction: field === 'lastSeenAt' || field === 'updatedAt' ? 'desc' : 'asc' });
    else onSort({ field, direction: effective.direction === 'asc' ? 'desc' : 'asc' });
  };

  if (loading && !hits.length) {
    return (
      <div className="divide-y divide-line" aria-busy="true" aria-label="Loading results">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-3.5"><Skeleton className="h-5 w-20 rounded-full" /><Skeleton className="w-40" /><Skeleton className="hidden w-48 md:block" /><span className="flex-1" /><Skeleton className="w-16" /></div>
        ))}
      </div>
    );
  }

  return (
    <div className={cn('transition-opacity', dimmed && 'opacity-60')}>
      <table className="hidden w-full table-fixed border-collapse md:table">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="border-b border-line text-left text-[12px] text-ink-3">
            {citations && <th className="w-10 py-2 pl-4 font-medium"><span className="sr-only">Citation</span></th>}
            {columns.map((c, i) => (
              <th key={c.key} scope="col" className={cn('py-2 pr-3 font-medium', i === 0 && !citations && 'pl-4', c.className)}
                aria-sort={c.sort && effective.field === c.sort ? (effective.direction === 'asc' ? 'ascending' : 'descending') : undefined}>
                {c.sort && onSort ? (
                  <button type="button" onClick={() => toggleSort(c.sort!)} className="inline-flex items-center gap-1 rounded hover:text-ink">
                    {c.label}
                    {effective.field === c.sort && (effective.direction === 'asc' ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />)}
                  </button>
                ) : c.label}
              </th>
            ))}
            <th className="w-8" aria-hidden />
          </tr>
        </thead>
        <tbody>
          {hits.map((hit, index) => (
            <tr key={hit.id} ref={el => { rows.current[index] = el; }} tabIndex={index === 0 || hit.id === selectedId ? 0 : -1}
              aria-selected={hit.id === selectedId} onClick={() => activate(hit)} onDoubleClick={() => open(hit)} onKeyDown={e => onKey(e, index, hit)}
              data-record={hit.id}
              className={cn('group cursor-pointer border-b border-line/70 outline-none transition-colors last:border-0 focus-visible:bg-select',
                hit.id === selectedId ? 'bg-select' : 'hover:bg-hover')}>
              {citations && <td className="py-2.5 pl-4"><Cite n={citations.get(hit.id)} /></td>}
              {columns.map((c, i) => <td key={c.key} className={cn('py-2.5 pr-3 align-middle', i === 0 && !citations && 'pl-4')}>{c.render(hit)}</td>)}
              <td className="pr-3 text-ink-3"><ChevronRight className="size-4 opacity-0 transition-opacity group-hover:opacity-100" /></td>
            </tr>
          ))}
        </tbody>
      </table>
      <ul className="divide-y divide-line md:hidden" aria-label={caption}>
        {hits.map(hit => {
          const Icon = kindIcon[hit.kind];
          return (
            <li key={hit.id}>
              <Link to={recordPath(mspId, hit.kind, hit.id)} onClick={() => remember(hit)} data-record={hit.id} className="flex items-center gap-3 px-4 py-3 outline-none active:bg-hover focus-visible:bg-select">
                {citations && <Cite n={citations.get(hit.id)} />}
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    {hit.kind !== 'device' && <Icon className="size-4 shrink-0 text-ink-3" />}
                    <span className={cn('truncate font-medium', hit.kind === 'device' && 'font-mono text-[13px]')}>{hit.title}</span>
                  </span>
                  <span className="mt-0.5 block truncate text-[12.5px] text-ink-3">{recordContext(hit) || 'Customer'}</span>
                </span>
                {hit.kind === 'device' && (
                  <span className="flex flex-col items-end gap-1"><StatusBadge status={hit.status} compact /><TimeAgo value={hit.lastSeenAt} compact className="text-[12px]" /></span>
                )}
                <ChevronRight className="size-4 shrink-0 text-ink-3" />
              </Link>
              {hit.kind === 'device' && <span className="block px-4 pb-2"><DeviceIdentity device={hit} /></span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function Cite({ n, onHover }: { n?: number; onHover?: (on: boolean) => void }) {
  if (!n) return null;
  return (
    <span onMouseEnter={() => onHover?.(true)} onMouseLeave={() => onHover?.(false)}
      className="tabular inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-[5px] bg-brand-soft px-1 text-[11px] font-semibold text-brand-ink">{n}</span>
  );
}
